import { useEffect, useRef, useState } from "react";
import { userInitials } from "./projectPlanUtils";

/**
 * Horizontal bar across day columns — drag to move, resize edges to change span.
 * startIdx/endIdx are inclusive day indices in the visible range.
 * onChange is called once when the drag ends.
 */
export function PlanSpanBar({
  startIdx,
  endIdx,
  dayWidth,
  color,
  title,
  users = [],
  selected = false,
  conflict = false,
  readOnly = false,
  onChange,
  onSelect,
  compact = false,
  summary = false,
  disableResize = false,
}) {
  const [drag, setDrag] = useState(null);
  const [preview, setPreview] = useState(null);
  const previewRef = useRef(null);
  const movedRef = useRef(false);

  const s = preview?.startIdx ?? startIdx;
  const e = preview?.endIdx ?? endIdx;

  const barColor = conflict ? "#ef4444" : color;
  const left = s * dayWidth + 4;
  const width = Math.max(dayWidth - 8, (e - s + 1) * dayWidth - 8);

  useEffect(() => {
    if (!drag || readOnly) return undefined;

    // Pixel slop: tiny jitter still counts as a click; a real same-day drag
    // (moved far but stayed on the same column) must not open onSelect.
    const clickSlopPx = 6;
    let maxAbsDx = 0;

    const onMove = (ev) => {
      const x = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const dx = x - drag.startX;
      maxAbsDx = Math.max(maxAbsDx, Math.abs(dx));
      const dayDelta = Math.round(dx / dayWidth);

      let nextS = drag.s0;
      let nextE = drag.e0;

      if (drag.kind === "move") {
        const len = drag.e0 - drag.s0;
        nextS = drag.s0 + dayDelta;
        nextE = nextS + len;
        if (nextS < 0) {
          nextS = 0;
          nextE = len;
        }
      } else if (drag.kind === "l") {
        nextS = Math.min(drag.e0, drag.s0 + dayDelta);
      } else if (drag.kind === "r") {
        nextE = Math.max(drag.s0, drag.e0 + dayDelta);
      }

      const indicesChanged = nextS !== drag.s0 || nextE !== drag.e0;
      movedRef.current = indicesChanged || maxAbsDx >= clickSlopPx;

      const next = { startIdx: nextS, endIdx: nextE };
      previewRef.current = next;
      setPreview(next);
    };

    const onUp = () => {
      const committed = previewRef.current;
      const indicesChanged =
        committed &&
        (committed.startIdx !== drag.s0 || committed.endIdx !== drag.e0);
      movedRef.current = Boolean(indicesChanged) || maxAbsDx >= clickSlopPx;
      if (committed && onChange && indicesChanged) {
        onChange(committed.startIdx, committed.endIdx);
      }
      previewRef.current = null;
      setPreview(null);
      setDrag(null);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [drag, dayWidth, onChange, readOnly]);

  const startDrag = (kind) => (ev) => {
    if (summary) return;
    if (readOnly && !onSelect) return;
    ev.stopPropagation();
    if (readOnly && onSelect) {
      // Click-only mode (e.g. resource day dialog) — no drag.
      return;
    }
    ev.preventDefault();
    const x = ev.touches ? ev.touches[0].clientX : ev.clientX;
    movedRef.current = false;
    setPreview(null);
    setDrag({ kind, startX: x, s0: startIdx, e0: endIdx });
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (summary) return;
    if (onSelect && !movedRef.current) {
      onSelect();
    }
    movedRef.current = false;
  };

  const displayUsers = users.slice(0, 3);
  const clickable = Boolean(onSelect) && !summary;
  const cursorStyle = summary
    ? "default"
    : clickable && (readOnly || !onChange)
      ? "pointer"
      : readOnly
        ? "default"
        : drag?.kind === "move"
          ? "grabbing"
          : "grab";

  return (
    <div
      role="presentation"
      onMouseDown={startDrag("move")}
      onTouchStart={startDrag("move")}
      onClick={handleClick}
      className={`task-block ${summary ? "task-block--summary" : ""} ${drag ? "dragging" : ""} ${selected ? "ring-2 ring-white/60" : ""}`}
      style={{
        position: "absolute",
        left,
        width,
        top: compact ? 4 : 8,
        bottom: compact ? 4 : 8,
        backgroundColor: barColor,
        opacity: summary ? 0.42 : 1,
        cursor: cursorStyle,
        zIndex: summary ? 0 : selected || drag ? 3 : 1,
        fontSize: compact ? 11 : 13,
        pointerEvents: summary ? "none" : undefined,
      }}
      title={title}
    >
      {!readOnly && !summary && !disableResize ? (
        <>
          <div
            className="resize-handle left"
            onMouseDown={startDrag("l")}
            onTouchStart={startDrag("l")}
          />
          <div
            className="resize-handle right"
            onMouseDown={startDrag("r")}
            onTouchStart={startDrag("r")}
          />
        </>
      ) : null}
      {summary ? (
        <>
          {title ? (
            <span className="tb-title min-w-0 flex-1 truncate text-[12px] font-medium">
              {title}
            </span>
          ) : null}
          {displayUsers.length > 0 ? (
            <span className="tb-avatars flex shrink-0">
              {displayUsers.map((u) => (
                <span
                  key={u.id}
                  className="av inline-flex h-[20px] w-[20px] items-center justify-center rounded-full text-[9px] font-semibold text-white"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.35)",
                    boxShadow: "0 0 0 1.5px rgba(255,255,255,0.85)",
                    marginLeft: displayUsers.indexOf(u) === 0 ? 0 : -5,
                  }}
                >
                  {userInitials(u.displayTitle || u.name)}
                </span>
              ))}
            </span>
          ) : null}
        </>
      ) : !compact ? (
        <>
          {title ? (
            <span className="tb-title min-w-0 flex-1 truncate">{title}</span>
          ) : null}
          {displayUsers.length > 0 ? (
            <span className="tb-avatars flex shrink-0">
              {displayUsers.map((u) => (
                <span
                  key={u.id}
                  className="av inline-flex h-[22px] w-[22px] items-center justify-center rounded-full text-[10px] font-semibold text-white"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.25)",
                    boxShadow: "0 0 0 1.5px rgba(255,255,255,0.85)",
                    marginLeft: displayUsers.indexOf(u) === 0 ? 0 : -6,
                  }}
                >
                  {userInitials(u.displayTitle || u.name)}
                </span>
              ))}
            </span>
          ) : null}
        </>
      ) : title ? (
        <span className="truncate px-1 text-[11px] font-medium">{title}</span>
      ) : null}
    </div>
  );
}
